import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChefHat, ShieldCheck, Hammer, ChevronRight } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useSidebar } from "./ui/use-sidebar";
import { SidebarUserMenu } from "./layout/SidebarUserMenu";
import { useAuth } from "@/hooks/useAuth";
import { useFeatures } from "@/hooks/useFeatures";
import { useIsTablet } from "@/hooks/use-mobile";
import { useAppMode } from "@/contexts/AppModeContext";
import type { NavSectionConfig, NavItemConfig } from "@/lib/nav-builder";
import { NAV } from "@/lib/nav-config";
import { YesChefLogo } from "@/components/common/YesChefLogo";
import { openSupportChat } from "@/lib/support-chat";


// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// One nav-item style, driven entirely off `data-[active=true]` (set from the
// isActive prop by the cva base). The rail tile size/centering comes from the
// cva base too — no !important overrides needed here. Measurements per Figma:
// 44px tile, 16px padding, 24px icon, 12px icon↔label gap.
// stroke-width 1.5 keeps the 24px icons as light as the 16px ones elsewhere
// (lucide's default 2px stroke looks heavy at 24px).
const NAV_ITEM_BASE = "h-11 rounded-lg px-4 gap-3 [&_svg]:text-current [&_svg]:[stroke-width:1.5] [&>svg]:size-6";

const NAV_ITEM_STATE = cn(
  // inactive: regular weight, muted
  "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-accent-foreground",
  // active: solid white tile, dark text, medium weight (expanded + rail alike)
  "data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium data-[active=true]:hover:bg-sidebar-primary",
  // open dropdown parity
  "data-[state=open]:bg-sidebar-hover",
);

// Sub-items sit one level in: shorter tile, no icon, same active treatment so a
// selected child reads the same as a selected top-level item.
const NAV_SUB_ITEM = cn(
  "h-9 rounded-lg px-3 text-base",
  "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-accent-foreground",
  "data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium data-[active=true]:hover:bg-sidebar-primary",
);

// ---------------------------------------------------------------------------
// NavMenu — renders built sections, filtered by permissions
// ---------------------------------------------------------------------------

function NavMenu({ sections }: { sections: NavSectionConfig[] }) {
  const { t } = useLingui();
  const { hasPermission, hasRole, user } = useAuth();
  const { features } = useFeatures();
  const { mode } = useAppMode();
  const { state } = useSidebar();
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const isAllowed = (permission?: string | string[]) => {
    if (!permission) return true;
    const perms = Array.isArray(permission) ? permission : [permission];
    return perms.some((p) => hasPermission(p));
  };

  const isRouteActive = (url?: string) =>
    !!url && (currentPath === url || currentPath.startsWith(`${url}/`));

  const isSectionAllowed = (rolesAny?: string[]) => {
    if (!rolesAny?.length) return true;
    return rolesAny.some((role) => hasRole(role));
  };

  const isModeVisible = (modes?: string[]) => !modes?.length || modes.includes(mode);

  const isItemVisible = (item: NavItemConfig): boolean => {
    if (!isAllowed(item.permission) || !isModeVisible(item.modes)) return false;
    // What the tenant has, not what this user may do (T-1106).
    if (item.feature && !features[item.feature]) return false;
    // A group with nothing the user may see is nothing at all.
    return item.items ? item.items.some(isItemVisible) : true;
  };

  // The rail is icons only, so a group header would hide its children behind
  // something that has no room to expand. Show the leaves instead.
  const railItems = (items: NavItemConfig[]) =>
    items.flatMap((item) => item.items ?? [item]);

  // Sections marked requiresOrgContext are hidden when the user has no active
  // organisation (e.g. a super-admin who hasn't "logged in as" a specific org).
  const hasOrgContext = user?.organization?.id != null;

  const isRail = state === "collapsed";

  const visibleSections = sections.filter((s) =>
    isSectionAllowed(s.rolesAny) &&
    isModeVisible(s.modes) &&
    (!s.requiresOrgContext || hasOrgContext) &&
    s.items.some(isItemVisible),
  );

  return (
    <>
      {visibleSections.map((section, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <SidebarSeparator className="mx-5 bg-sidebar-border" />}
          <SidebarGroup className="gap-3 px-5 group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0">
            <SidebarGroupLabel
              className={
                section.internal
                    ? "text-(--color-warning)/80 text-sm font-normal flex items-center gap-1"
                    : "text-sidebar-muted text-sm font-normal"
              }
            >
              {section.internal && <ShieldCheck className="h-3 w-3 shrink-0" />}
              {t(section.title)}
            </SidebarGroupLabel>

            <SidebarGroupContent>
              <SidebarMenu>
                {(isRail ? railItems(section.items) : section.items)
                  .filter(isItemVisible)
                  .map((item, itemIdx) =>
                    item.items ? (
                      <NavGroup
                        key={`group-${itemIdx}`}
                        item={item}
                        items={item.items.filter(isItemVisible)}
                        isRouteActive={isRouteActive}
                      />
                    ) : (
                      <NavItem
                        key={`${item.url}-${itemIdx}`}
                        item={item}
                        isActive={isRouteActive(item.url)}
                      />
                    ),
                  )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * Expandable group header. Opens itself when one of its children is the current
 * route, and stays wherever the user last put it otherwise.
 */
function NavGroup({
  item,
  items,
  isRouteActive,
}: {
  item: NavItemConfig;
  items: NavItemConfig[];
  isRouteActive: (url?: string) => boolean;
}) {
  const { t } = useLingui();
  const hasActiveChild = items.some((child) => isRouteActive(child.url));
  const [isOpen, setIsOpen] = React.useState(hasActiveChild);
  const contentId = React.useId();

  // Open on the navigation that made a child current, rather than after it in a
  // effect: that renders the group closed for a frame and then reopens it,
  // which reads as a flicker on every route change into the group. Adjusting
  // during render is React's own answer for state that follows a prop, and it is
  // the same shape LabelingTab uses to follow the server's row.
  const [wasActive, setWasActive] = React.useState(hasActiveChild);
  if (hasActiveChild !== wasActive) {
    setWasActive(hasActiveChild);
    // Only the opening is automatic. Losing the active child leaves the group
    // where the user last put it, which is what the header comment promises.
    if (hasActiveChild) setIsOpen(true);
  }

  if (!items.length) return null;

  const Icon = item.icon;
  const label = t(item.title);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setIsOpen((open) => !open)}
        // Folded away, the header carries the active state of the child inside
        // it; open, the child shows it itself.
        isActive={hasActiveChild && !isOpen}
        tooltip={label}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className={cn(NAV_ITEM_BASE, NAV_ITEM_STATE)}
      >
        <Icon className="size-6" />
        <span className="text-base group-data-[collapsible=icon]:hidden">{label}</span>
        <ChevronRight
          className={cn(
            "ml-auto size-4 transition-transform group-data-[collapsible=icon]:hidden",
            isOpen && "rotate-90",
          )}
        />
      </SidebarMenuButton>

      {isOpen && (
        <SidebarMenuSub id={contentId}>
          {items.map((child) => (
            <SidebarMenuSubItem key={child.url}>
              <SidebarMenuSubButton
                asChild
                isActive={isRouteActive(child.url)}
                className={NAV_SUB_ITEM}
              >
                <Link to={child.url ?? "#"}>
                  <span>{t(child.title)}</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

function NavItem({
  item,
  isActive,
}: {
  item: NavItemConfig;
  isActive: boolean;
}) {
  const { t } = useLingui();
  const { isMobile, setOpenMobile } = useSidebar();

  const Icon = item.icon;
  const isDisabled = item.external || item.disabled;
  const label = t(item.title);

  // An action item opens something instead of navigating (the support chat).
  if (item.action) {
    const handleClick = () => {
      // The mobile sidebar is a sheet; close it so it does not sit over the chat.
      if (isMobile) setOpenMobile(false);
      if (!openSupportChat()) {
        toast.error(t`Chat is unavailable right now. Please try again in a moment.`);
      }
    };
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          type="button"
          tooltip={label}
          onClick={handleClick}
          className={cn(NAV_ITEM_BASE, NAV_ITEM_STATE)}
        >
          <Icon className="size-6" />
          <span className="text-base group-data-[collapsible=icon]:hidden">{label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={label}
        className={cn(
          NAV_ITEM_BASE,
          NAV_ITEM_STATE,
          isDisabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {isDisabled ? (
          <button type="button" disabled className="cursor-not-allowed">
            <Icon className="size-6" />
            <span className="text-base group-data-[collapsible=icon]:hidden">{label}</span>
            {item.badge && (
              <span className="ml-auto text-[0.625rem] px-1.5 py-0.5 rounded-full bg-sidebar-primary text-sidebar-primary-foreground font-bold leading-none group-data-[collapsible=icon]:hidden">
                {item.badge}
              </span>
            )}
          </button>
        ) : (
          <Link to={item.url ?? "#"}>
            <Icon className="size-6" />
            <span className="text-base group-data-[collapsible=icon]:hidden">{label}</span>
            {item.badge && (
              <span className="ml-auto text-[0.625rem] px-1.5 py-0.5 rounded-full bg-sidebar-primary text-sidebar-primary-foreground font-bold leading-none group-data-[collapsible=icon]:hidden">
                {item.badge}
              </span>
            )}
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ---------------------------------------------------------------------------
// SidebarModeToggle — Working / Build switch for the compact rail (tablet),
// where the TopBar mode toggle isn't shown. Vertical segmented control; the
// active mode gets the same white tile as an active nav item in the rail.
// ---------------------------------------------------------------------------

function SidebarModeToggle() {
  const { t } = useLingui();
  const { mode, setMode } = useAppMode();

  const options = [
    { value: "working" as const, label: t`Working`, icon: ChefHat },
    { value: "build" as const, label: t`Build`, icon: Hammer },
  ];

  return (
    // Expanded: one row of two labelled buttons (segmented control).
    // Compact: a stack of two icon tiles, centered to line up with the nav icons.
    <div className="flex gap-1 rounded-lg bg-sidebar-hover p-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0">
      {options.map(({ value, label, icon: Icon }) => {
        const isActive = mode === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            title={label}
            aria-pressed={isActive}
            className={cn(
              "flex h-9 flex-1 items-center justify-center gap-2 rounded-md px-2 text-sm font-medium transition-colors",
              "group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:rounded-lg group-data-[collapsible=icon]:px-0",
              isActive
                ? "bg-sidebar-primary text-sidebar-primary-foreground"
                : "text-sidebar-muted hover:text-sidebar-foreground",
            )}
          >
            <Icon className="size-4 shrink-0" />
            <span className="group-data-[collapsible=icon]:hidden">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AppSidebar
// ---------------------------------------------------------------------------

export function AppSidebar({ className, ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useLingui();
  const { state } = useSidebar();
  const isTablet = useIsTablet();

  return (
    // Borderless: the nav sits on the dark canvas and the content card's own
    // border/shadow provides the separation, so drop the variant's right rule.
    <Sidebar collapsible="icon" className={cn("group-data-[side=left]:border-r-0", className)} {...props}>
      <SidebarHeader className="m-0 p-5 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
        <SidebarMenu>
          <SidebarMenuItem>
            {/* Expanded: logo + wordmark on the left, toggle on the right.
                Compact rail: the logo is replaced by the (centered) expand
                toggle, sized to match the icon tiles. */}
            <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 overflow-hidden rounded-md transition-opacity hover:opacity-80 group-data-[collapsible=icon]:hidden"
              >
                <YesChefLogo />
                <span
                  className="font-semibold text-2xl text-sidebar-foreground"
                  style={{ lineHeight: "var(--line-height-logo)" }}
                >
                  {t`Yes, Chef!`}
                </span>
              </Link>
              {/* Collapse is disabled on desktop — the rail toggle only exists
                  on tablet (xl = 1280px, the desktop breakpoint). */}
              <SidebarTrigger className="group-data-[collapsible=icon]:size-11! xl:hidden" />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-4">
        <NavMenu sections={NAV} />
      </SidebarContent>

      <SidebarFooter className="p-5 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
        {/* On tablet the page owns the top bar, so the mode toggle + profile
            menu live here in the rail. On desktop the TopBar owns both. */}
        {isTablet && <SidebarModeToggle />}
        {isTablet && <SidebarUserMenu />}
        {state !== "collapsed" && (
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-sidebar-footer">
            <button type="button" disabled className="cursor-not-allowed hover:text-sidebar-muted">
              {t`Legal`}
            </button>
            <span aria-hidden>•</span>
            <button type="button" disabled className="cursor-not-allowed hover:text-sidebar-muted">
              {t`Privacy Center`}
            </button>
            <span aria-hidden>•</span>
            <button type="button" disabled className="cursor-not-allowed hover:text-sidebar-muted">
              {t`Privacy Policy`}
            </button>
            <span aria-hidden>•</span>
            <button type="button" disabled className="cursor-not-allowed hover:text-sidebar-muted">
              {t`Cookies`}
            </button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
